import { OvenMediaRecordSession, OvenMediaRecordSessionStatus } from "../interfaces/context";

// What a record offers as an MP4. A middleware started with VOD_BUILD_MP4_ENABLED=false stops its
// automatic build at TERMINATED with the m3u8 and no mp4: the record is a raw backup (segments +
// file.xml + file.m3u8) that "Générer" turns into a file. TERMINATED is written in exactly two
// places — that skip, and a successful build, which always sets mp4 — so TERMINATED without mp4
// is raw. An mp4 wins over any status: since middleware 5.6.0 a rebuild writes beside the old
// file.mp4 and only replaces it on success, so a rebuilding or failed record still has its file.
export enum RecordMp4State {
  BUILT = "built",
  RAW = "raw",
  RECORDING = "recording",
  BUILDING = "building",
  ERROR = "error",
  NONE = "none",
}

type RecordFields = Pick<OvenMediaRecordSession, "status" | "mp4" | "deleted">;

export const getRecordMp4State = (session: Pick<RecordFields, "status" | "mp4">): RecordMp4State => {
  if (session.mp4) return RecordMp4State.BUILT;
  switch (session.status) {
    case OvenMediaRecordSessionStatus.TERMINATED:
      return RecordMp4State.RAW;
    case OvenMediaRecordSessionStatus.INITIALIZED:
    case OvenMediaRecordSessionStatus.RECORDING:
      return RecordMp4State.RECORDING;
    case OvenMediaRecordSessionStatus.BUILDING_M3U8:
    case OvenMediaRecordSessionStatus.BUILDING_MP4:
      return RecordMp4State.BUILDING;
    case OvenMediaRecordSessionStatus.ERROR_XML:
    case OvenMediaRecordSessionStatus.ERROR_BUILD_M3U8:
    case OvenMediaRecordSessionStatus.ERROR_BUILD_MP4:
      return RecordMp4State.ERROR;
    default:
      return RecordMp4State.NONE;
  }
};

export const isRawRecord = (session: Pick<RecordFields, "status" | "mp4">): boolean => getRecordMp4State(session) === RecordMp4State.RAW;

// Display only: the search and the sort run server-side on the stored status, so "raw" is never
// searchable and sorts as "terminated".
export const getRecordStatusLabel = (session: Pick<RecordFields, "status" | "mp4">): string => (isRawRecord(session) ? "raw" : session.status);

// The hover message of a record's dimmed MP4 icon; undefined when it has an mp4 (active icon).
export const getRecordMp4Message = (session: RecordFields): string | undefined => {
  switch (getRecordMp4State(session)) {
    case RecordMp4State.BUILT:
      return undefined;
    case RecordMp4State.RAW:
      return session.deleted ? "MP4 non construit (record raw)" : "MP4 non construit (record raw) — Générer pour le produire";
    case RecordMp4State.RECORDING:
      return "Enregistrement en cours";
    case RecordMp4State.BUILDING:
      return "MP4 en cours de construction";
    case RecordMp4State.ERROR:
      return "Construction du MP4 en échec";
    default:
      return "Pas de MP4";
  }
};

// A deleted record has no files left: the build would fail on file.xml and overwrite its status
// with error_xml.
export const canBuildRecord = (session: Pick<RecordFields, "status" | "deleted">): boolean =>
  !session.deleted && session.status !== OvenMediaRecordSessionStatus.INITIALIZED && session.status !== OvenMediaRecordSessionStatus.EXPIRED;
